// Maps to backend/routers/budget.py
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { BudgetSummary } from '../types/budget';
import { showErrorNotification } from '../utils/notificationUtils';
import { useCallback } from 'react';

// Cache for storing ETags
const etagCache = new Map<string, string>();

export const useBudgetPlan = (accessToken: string, options: { isVisible?: boolean } = {}) => {
    const { isVisible = true } = options;
    const queryClient = useQueryClient();

    const {
        data: plan,
        isLoading: loading,
        error: queryError,
        refetch
    } = useQuery({
        queryKey: ['budget', 'plan'],
        queryFn: async (): Promise<BudgetSummary | null> => {
            try {
                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${accessToken}`,
                };

                // Add If-None-Match header if we have a cached ETag
                const cachedETag = etagCache.get('budget_plan');
                if (cachedETag) {
                    headers['If-None-Match'] = cachedETag;
                }

                const response = await fetch(`${env.API_URL}/api/budget/plan`, {
                    headers,
                    credentials: 'include',
                    cache: 'no-cache', // Forces validation with server
                });

                // If we get a 304 Not Modified, use the cached data
                if (response.status === 304) {
                    console.log('Using cached budget plan data (304 Not Modified)');
                    // Ensure the cached data type matches BudgetSummary | null
                    const cachedData = queryClient.getQueryData<BudgetSummary | null>(['budget', 'plan']);
                    return cachedData || null;
                }

                // Store the new ETag for future requests
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    etagCache.set('budget_plan', newETag);
                }

                if (!response.ok) {
                    if (response.status !== 404) {
                        // Try to get a detailed error message
                        try {
                            const errorData = await response.json();
                            throw new Error(errorData.detail || 'Failed to fetch budget plan');
                        } catch (jsonError) {
                            throw new Error('Failed to fetch budget plan');
                        }
                    }
                    return null; // For 404, return null (no plan exists)
                }

                const data: BudgetSummary = await response.json();
                // The backend now returns a plan that might not have a "strategy" object.
                // BudgetSummary type is already updated to not expect strategy.
                // If backend sends a `name` field for the plan (e.g. "My Budget"), it will be mapped.
                // If not, and BudgetSummary expects a name, we might need to provide a default here or in component.
                // For now, assuming `data` conforms to the new `BudgetSummary` type.
                return data;
            } catch (err) {
                console.error('Error fetching budget plan:', err);
                const errorMessage = err instanceof Error ? err.message : 'Failed to load budget data';

                showErrorNotification(err, 'Could not load your budget data. Please try again later.');
                throw new Error(errorMessage); // Rethrow to be caught by React Query
            }
        },
        enabled: isVisible && !!accessToken, // Ensure accessToken is present
        staleTime: 300000, // 5 minutes
        // gcTime: 600000, // Consider gcTime if needed (defaults to 5 mins)
    });

    // Extract error message from query error
    const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

    // Maintain API compatibility
    const refreshPlan = useCallback(async () => {
        // This will mark ['budget', 'plan'] as stale and trigger a refetch for active queries.
        await queryClient.invalidateQueries({ queryKey: ['budget', 'plan'] });
    }, [queryClient]);

    return {
        plan,
        loading,
        error,
        refreshPlan
    };
}; 